import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type BillingMachineResource,
  BillingMachinesController_register_v1,
  type BillingMachinesController_register_v1Body,
  type OutletResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { machineNameSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { currentBusinessDate } from '../reporting/reporting-period';
import { DayField, OutletField } from './machine-fields';

/**
 * Registering a billing machine: `POST /v1/billing-machines`, owner-only.
 *
 * **The machine ID is text and stays text.** `0946014363` and `946014363` are
 * two machines on the API, so the field is a plain text input with a numeric
 * keyboard hint — never `type="number"`, which would drop the leading zero —
 * and nothing here parses it. `MACHINE_CODE_TAKEN` lands on that field.
 */

const MACHINE_CODE_REQUIRED = 'Enter the machine ID its export prints.';
const MACHINE_CODE_MALFORMED = 'A machine ID is 1 to 20 digits, exactly as the export prints it.';
const OUTLET_REQUIRED = 'Choose the outlet this machine sells for.';
const DAY_REQUIRED = 'Choose the day it starts selling for that outlet.';

const registerSchema = z.object({
  machineCode: z
    .string()
    .trim()
    .min(1, MACHINE_CODE_REQUIRED)
    .regex(/^\d{1,20}$/, MACHINE_CODE_MALFORMED),
  name: machineNameSchema,
  outletId: z.string().min(1, OUTLET_REQUIRED),
  attachedFrom: z.string().min(1, DAY_REQUIRED),
});

type RegisterValues = z.infer<typeof registerSchema>;

export function RegisterMachineDialog({
  open,
  onOpenChange,
  outlets,
  defaultOutletId,
  onRegistered,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The outlets still trading — the only ones a machine may be attached to. */
  outlets: readonly OutletResource[];
  defaultOutletId: string | null;
  onRegistered: (machine: BillingMachineResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Register machine</DialogTitle>
          <DialogDescription>
            One stand-alone billing machine, and the outlet it sells for. Bills in its export are
            booked to that outlet from the day you choose.
          </DialogDescription>
        </DialogHeader>

        {/* A child, so closing the dialog unmounts the form — and its key. */}
        <RegisterMachineForm
          outlets={outlets}
          defaultOutletId={defaultOutletId}
          onRegistered={onRegistered}
        />
      </FormDialogContent>
    </Dialog>
  );
}

function RegisterMachineForm({
  outlets,
  defaultOutletId,
  onRegistered,
}: {
  outlets: readonly OutletResource[];
  defaultOutletId: string | null;
  onRegistered: (machine: BillingMachineResource) => void;
}) {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);

  const form = useForm({
    resolver: zodResolver(registerSchema),
    mode: 'onTouched',
    defaultValues: {
      machineCode: '',
      name: '',
      outletId:
        defaultOutletId !== null && outlets.some((outlet) => outlet.id === defaultOutletId)
          ? defaultOutletId
          : outlets.length === 1
            ? (outlets[0]?.id ?? '')
            : '',
      attachedFrom: today,
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // The key belongs to the body: held while it stands, so a retry after a lost
  // answer replays; fresh once it changes, so a corrected field after a failed
  // attempt is not refused as a reused key. See `lib/write-attempt.ts`.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;
  const nowhereToPutIt = outlets.length === 0;

  const handleSubmit = async (values: RegisterValues) => {
    if (pending || nowhereToPutIt) return;

    setFormError(null);

    try {
      const body: BillingMachinesController_register_v1Body = values;
      const sending = attemptFor(attempt.current, JSON.stringify(body));
      attempt.current = sending;

      onRegistered(
        await client.request(BillingMachinesController_register_v1, {
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      const code = error instanceof ApiError ? error.code : null;

      if (code === 'MACHINE_CODE_TAKEN') {
        form.setError('machineCode', { message: messageForFailure(error) });
        form.setFocus('machineCode');
      } else if (code === 'RESOURCE_NOT_FOUND') {
        // The outlet was retired or removed while this form was open.
        form.setError('outletId', { message: messageForFailure(error) });
      } else {
        setFormError(messageForFailure(error));
      }
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="machineCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Machine ID
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // Text, with a digit keyboard hint. `type="number"` would
                    // turn 0946014363 into 946014363 — a different machine.
                    type="text"
                    inputMode="numeric"
                    className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                    required
                    placeholder="0946014363"
                    autoComplete="off"
                    spellCheck={false}
                    {...field}
                  />
                }
              />
              <FormDescription>
                Exactly as the machine’s export prints it, leading zeros included.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    required
                    placeholder="Boating counter machine"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>How staff refer to it — the counter it sits at.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <OutletField control={form.control} name="outletId" outlets={outlets} idPrefix="register" />

        <DayField
          control={form.control}
          name="attachedFrom"
          label="Sells for it from"
          maxDay={today}
          idPrefix="register"
          description="Bills dated from this day are booked to that outlet. Not later than today."
        />

        {nowhereToPutIt && (
          <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
            A machine sells for an outlet, and no outlet is trading. Create an outlet first, then
            come back and register the machine.
          </p>
        )}

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending || nowhereToPutIt}>
            {pending ? 'Registering…' : 'Register machine'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
